import { useState, useEffect } from "react";
import DevAvatarAnimation from "@/components/auth/DevAvatarAnimation";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, ArrowRight, ArrowLeft, Loader2, Phone, Mail, Hash, ShieldCheck, User, AlertTriangle } from "lucide-react";
import { validateEmail } from "@/lib/emailValidation";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import logo from "@/assets/logo.webp";

type Step = "lookup" | "confirm" | "otp" | "password" | "success";

const STEPS: { key: Step; label: string }[] = [
  { key: "lookup", label: "Verify Identity" },
  { key: "confirm", label: "Confirm Details" },
  { key: "otp", label: "Verify Phone" },
  { key: "password", label: "Set Password" },
  { key: "success", label: "Complete" },
];

const StudentSignup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("lookup");
  const [loading, setLoading] = useState(false);

  // Step 1 fields
  const [formIvIndex, setFormIvIndex] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  // Pre-reg data
  const [preRegData, setPreRegData] = useState<any>(null);
  const [nacteData, setNacteData] = useState<any>(null);

  // OTP
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [devOtp, setDevOtp] = useState("");

  // Password
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Email validation
  const [emailError, setEmailError] = useState("");
  const [emailSuggestion, setEmailSuggestion] = useState("");

  // Result
  const [studentNumber, setStudentNumber] = useState("");

  // Resend cooldown (seconds remaining)
  const [resendCooldown, setResendCooldown] = useState(0);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const progress = ((stepIndex + 1) / STEPS.length) * 100;

  const handleLookup = async () => {
    if (!formIvIndex.trim() || !email.trim() || !mobileNumber.trim()) {
      toast({ title: "All fields are required", variant: "destructive" });
      return;
    }

    // Validate email
    const emailValidation = validateEmail(email.trim());
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error || "Invalid email");
      if (emailValidation.suggestion) {
        setEmailSuggestion(emailValidation.suggestion);
      }
      toast({ title: "Invalid Email", description: emailValidation.error, variant: "destructive" });
      return;
    }
    setEmailError("");
    setEmailSuggestion("");
    setLoading(true);
    try {
      // Normalize Form IV index: trim, uppercase, strip leading "N" if followed by "S"
      // (handles users who include the NACTEVET "N" prefix by mistake)
      const rawIndex = formIvIndex.trim().toUpperCase();
      const normalizedIndex = /^NS/.test(rawIndex) ? rawIndex.substring(1) : rawIndex;

      // Try normalized first, then raw as fallback
      let preReg: any = null;
      let lookupError: any = null;

      const { data: r1, error: e1 } = await supabase
        .rpc("lookup_pre_registration", { _form_iv_index: normalizedIndex });
      preReg = r1?.[0];
      lookupError = e1;

      if (!preReg && normalizedIndex !== rawIndex) {
        const { data: r2, error: e2 } = await supabase
          .rpc("lookup_pre_registration", { _form_iv_index: rawIndex });
        preReg = r2?.[0];
        lookupError = e2;
      }

      // Persist normalized value back to the input so downstream calls use it
      if (preReg && normalizedIndex !== formIvIndex.trim()) {
        setFormIvIndex(normalizedIndex);
      }

      if (lookupError || !preReg) {
        toast({
          title: "Not Pre-Registered",
          description: "Your Form IV index number is not found. Please contact the administration.",
          variant: "destructive",
        });
        return;
      }

      if (preReg.onboarding_status === "active") {
        toast({
          title: "Already Registered",
          description: "An account already exists for this Form IV index number. Please sign in.",
          variant: "destructive",
        });
        return;
      }

      setPreRegData(preReg);

      // NACTE lookup
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const nacteRes = await fetch(`https://${projectId}.supabase.co/functions/v1/nacte-lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_iv_index: formIvIndex.trim() }),
      });
      const nacteResult = await nacteRes.json();

      if (nacteResult.found) {
        setNacteData(nacteResult);
      }

      setStep("confirm");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAndSendOtp = async () => {
    setLoading(true);
    try {
      // Update pre-registration record with NACTE name, email, and mobile
      const updatedName = nacteData?.student_name || preRegData?.full_name;
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;

      // Note: pre-registration enrichment (name/email/phone) is now performed
      // server-side inside verify-otp AFTER OTP validation. We no longer make
      // an unauthenticated PATCH here, since that endpoint allowed tampering.

      // Update local preRegData with NACTE name
      if (nacteData?.student_name) {
        setPreRegData((prev: any) => ({ ...prev, full_name: nacteData.student_name }));
      }

      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form_iv_index: formIvIndex.trim(),
          mobile_number: mobileNumber.trim(),
        }),
      });
      const result = await res.json();

      if (!res.ok) {
        toast({ title: "Error", description: result.error, variant: "destructive" });
        return;
      }

      if (result._dev_otp) {
        setDevOtp(result._dev_otp);
      }

      setStep("otp");
      setResendCooldown(60);
      toast({ title: "OTP Sent", description: `A verification code has been sent to ${mobileNumber}` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Support paste of full code into any slot
    const digits = value.replace(/\D/g, "");
    if (digits.length > 1) {
      const newOtp = [...otp];
      for (let i = 0; i < digits.length && index + i < 6; i++) {
        newOtp[index + i] = digits[i];
      }
      setOtp(newOtp);
      const focusIdx = Math.min(index + digits.length, 5);
      document.getElementById(`otp-${focusIdx}`)?.focus();
      return;
    }
    const newOtp = [...otp];
    newOtp[index] = digits;
    setOtp(newOtp);

    if (digits && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      prev?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      toast({ title: "Enter all 6 digits", variant: "destructive" });
      return;
    }

    // For now, just move to password step — actual verification happens on final submit
    setStep("password");
  };


  const handleCreateAccount = async () => {
    if (password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form_iv_index: formIvIndex.trim(),
          otp: otp.join(""),
          email: email.trim(),
          password,
          full_name: nacteData?.student_name || preRegData?.full_name || "",
          mobile_number: mobileNumber.trim(),
          form_iv_results: nacteData?.form_iv_results || null,
          nactevet_reg_number: `N${formIvIndex.trim()}`,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        toast({ title: "Error", description: result.error, variant: "destructive" });
        return;
      }

      setStudentNumber(result.student_number);
      setStep("success");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(`https://${projectId}.supabase.co/functions/v1/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form_iv_index: formIvIndex.trim(),
          mobile_number: mobileNumber.trim(),
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        toast({ title: "Failed to resend", description: result.error, variant: "destructive" });
        return;
      }
      if (result._dev_otp) setDevOtp(result._dev_otp);
      setResendCooldown(60);
      toast({ title: "OTP Resent", description: "Check your phone for the new code." });
    } catch {
      toast({ title: "Failed to resend OTP", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg space-y-4">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto mb-3 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center p-2">
            <img src={logo} alt="St. David College" className="h-full w-full object-contain" />
          </div>
          <h1 className="text-xl font-bold">Student Registration</h1>
          <p className="text-sm text-muted-foreground">St. David College of Health Sciences</p>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            {STEPS.map((s, i) => (
              <span key={s.key} className={i <= stepIndex ? "text-primary font-medium" : ""}>
                {s.label}
              </span>
            ))}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step 1: Lookup */}
        {step === "lookup" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Hash className="h-5 w-5 text-primary" /> Verify Your Identity
              </CardTitle>
              <CardDescription>
                Enter your details to begin registration. You must be pre-registered by the administration.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="formIvIndex">Form IV Index Number</Label>
                <Input
                  id="formIvIndex"
                  placeholder="e.g., S0101/0001/2024"
                  value={formIvIndex}
                  onChange={(e) => setFormIvIndex(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Enter the Form IV index from your NECTA results slip (e.g. S1234/0056/2022).
                  Do not include the leading "N" — that belongs to your NACTEVET student number.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError("");
                    setEmailSuggestion("");
                  }}
                  className={emailError ? "border-destructive" : ""}
                />
                {emailError && (
                  <div className="flex items-start gap-2 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{emailError}</span>
                  </div>
                )}
                {emailSuggestion && (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm text-primary"
                    onClick={() => {
                      setEmail(emailSuggestion);
                      setEmailError("");
                      setEmailSuggestion("");
                    }}
                  >
                    Use {emailSuggestion} instead
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number</Label>
                <Input
                  id="mobile"
                  placeholder="0713664912"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                />
              </div>
              <Button className="w-full" onClick={handleLookup} disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
                Verify & Continue
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <button onClick={() => navigate("/login")} className="text-primary hover:underline">
                  Sign In
                </button>
              </p>
              <div className="flex justify-center pt-4">
                <DevAvatarAnimation />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Confirm */}
        {step === "confirm" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-primary" /> Confirm Your Details
              </CardTitle>
              <CardDescription>Please review your information before proceeding.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Full Name</span>
                  <span className="text-sm font-medium">{nacteData?.student_name || preRegData?.full_name || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Form IV Index</span>
                  <span className="text-sm font-medium">{formIvIndex}</span>
                </div>
                {(nacteData?.nactevet_reg_number || formIvIndex) && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">NACTEVET Reg. No.</span>
                    <span className="text-sm font-medium">{`N${formIvIndex}`}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Program</span>
                  <span className="text-sm font-medium">{preRegData?.program || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">NTA Level</span>
                  <span className="text-sm font-medium">{preRegData?.nta_level || "—"}</span>
                </div>
                {nacteData?.form_iv_results?.school && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Secondary School</span>
                    <span className="text-sm font-medium">{nacteData.form_iv_results.school}</span>
                  </div>
                )}
                {nacteData?.form_iv_results?.year && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Exam Year</span>
                    <span className="text-sm font-medium">{nacteData.form_iv_results.year}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Mobile</span>
                  <span className="text-sm font-medium">{mobileNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Email</span>
                  <span className="text-sm font-medium">{email}</span>
                </div>
              </div>

              {nacteData?.form_iv_results && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    Form IV Results
                    <Badge variant="secondary">Division {nacteData.form_iv_results.division}</Badge>
                    {nacteData.form_iv_results.points && (
                      <Badge variant="outline">{nacteData.form_iv_results.points} Points</Badge>
                    )}
                  </h4>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Subject</TableHead>
                          <TableHead className="text-xs">Grade</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nacteData.form_iv_results.subjects?.map((s: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs py-1.5">{s.name}</TableCell>
                            <TableCell className="text-xs py-1.5 font-medium">{s.grade}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep("lookup")} className="flex-1">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={handleConfirmAndSendOtp} disabled={loading} className="flex-1">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />}
                  Confirm & Send OTP
                </Button>
              </div>
              <div className="flex justify-center pt-4">
                <DevAvatarAnimation />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: OTP */}
        {step === "otp" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" /> Phone Verification
              </CardTitle>
              <CardDescription>
                Enter the 4-digit code sent to {mobileNumber.replace(/^(\d{4})\d{3}(\d{3})$/, "$1***$2")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {devOtp && (
                <div className="rounded-lg border border-accent-orange/30 bg-accent/5 p-3 text-center">
                  <p className="text-xs text-muted-foreground">Development Mode — OTP:</p>
                  <p className="text-2xl font-bold tracking-widest text-primary">{devOtp}</p>
                </div>
              )}

              <div className="flex justify-center gap-2">
                {otp.map((digit, i) => (
                  <Input
                    key={i}
                    id={`otp-${i}`}
                    className="w-11 h-12 text-center text-xl font-bold p-0 sm:w-12 sm:h-14 sm:text-2xl"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep("confirm")} className="flex-1">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={handleVerifyOtp} disabled={loading} className="flex-1">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Verify Code
                </Button>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Didn't receive the code?{" "}
                {resendCooldown > 0 ? (
                  <span className="text-muted-foreground">Resend in {resendCooldown}s</span>
                ) : (
                  <button
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="text-primary hover:underline disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                )}
              </p>
              <div className="flex justify-center pt-4">
                <DevAvatarAnimation />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Password */}
        {step === "password" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" /> Set Your Password
              </CardTitle>
              <CardDescription>Create a secure password for your account.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep("otp")} className="flex-1">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={handleCreateAccount} disabled={loading} className="flex-1">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                  Create Account
                </Button>
              </div>
              <div className="flex justify-center pt-4">
                <DevAvatarAnimation />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Success */}
        {step === "success" && (
          <Card>
            <CardContent className="py-8 text-center space-y-4">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-xl font-bold">Registration Complete!</h2>
              <p className="text-muted-foreground">
                Your student account has been created successfully.
              </p>
              {studentNumber && (
                <div className="rounded-lg border bg-muted/30 p-4">
                  <p className="text-sm text-muted-foreground">Your Student Number (NACTEVET Reg. No.)</p>
                  <p className="text-2xl font-bold text-primary">{studentNumber}</p>
                </div>
              )}
              <Button className="w-full" onClick={() => navigate("/login")}>
                Proceed to Sign In
              </Button>
              <p className="text-xs text-muted-foreground">You will be redirected to the sign-in page.</p>
              <div className="flex justify-center pt-4">
                <DevAvatarAnimation />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StudentSignup;
